import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * HauptSache 14.09.2026 (PageSpeed): Das Hintergrund-Video (ca. 7 MB) wird erst
 * geladen, wenn die Seite fertig geladen ist und der Browser Leerlauf hat.
 * Bis dahin ist das Vorschaubild (poster) sichtbar. Bei "Bewegung reduzieren"
 * oder aktiviertem Datensparmodus wird das Video gar nicht geladen
 * (CSS blendet es bei reduced-motion ohnehin aus).
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  connectedCallback() {
    super.connectedCallback();

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (navigator.connection && navigator.connection.saveData) return;

    const start = () => {
      if (!this.isConnected) return;
      const { videoSources, videoElement } = this.refs;

      for (const source of videoSources) {
        const { videoSource } = source.dataset;

        if (videoSource) source.setAttribute('src', videoSource);
      }

      videoElement.load();
    };

    const schedule = () => {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(start, { timeout: 3000 });
      } else {
        window.setTimeout(start, 1500);
      }
    };

    if (document.readyState === 'complete') {
      schedule();
    } else {
      window.addEventListener('load', schedule, { once: true });
    }
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
